/**
 * rice 的 HTTP 客户端。
 *
 * 刻意不复用 `#/lib/request` —— 那一层是围绕 core 的 `{code, message, data}`
 * 信封建的:它把 HTTP 状态码丢掉、把 401 当成全局登出信号、还要拆一层 data。
 * rice 用的是标准 REST 语义,套上那层反而要处处绕开。
 */
import {RICE_SERVICE} from '#/lib/constants'
import {get as getPersisted} from '#/state/persisted'

/** rice 返回的错误。`errors` 的形状是 {field: [msg]} 或 {detail: msg}。 */
export class RiceError extends Error {
  status: number
  errors: Record<string, string[] | string>

  constructor(status: number, errors: Record<string, string[] | string>) {
    super(RiceError.firstMessage(errors) || `rice 请求失败 (${status})`)
    this.name = 'RiceError'
    this.status = status
    this.errors = errors
  }

  /** 取第一条可展示的消息,给 toast 用。 */
  static firstMessage(errors: Record<string, string[] | string>): string {
    if (!errors) return ''
    if (typeof errors.detail === 'string') return errors.detail
    for (const value of Object.values(errors)) {
      if (typeof value === 'string') return value
      if (Array.isArray(value) && value.length) return String(value[0])
    }
    return ''
  }

  /** 某个字段是否有错 —— 表单内联提示用。 */
  fieldErrors(field: string): string[] {
    const value = this.errors?.[field]
    if (!value) return []
    return Array.isArray(value) ? value.map(String) : [String(value)]
  }
}

function currentToken(): string | undefined {
  const account = getPersisted('session')?.currentAccount
  if (account?.riceToken) return account.riceToken

  // 兜底:切换到 rice 之前登录的会话里只有 core 签的 daoJwt。rice 从
  // 2026-08-22 起在 `fetch_current_user` 里认这张老票 —— 不带上它的话,
  // 这些人页面能浏览(PDS 会话没坏)但每个要登录的接口都 401,而且前端
  // 大多把"取不到"当成空值,报出来的是些莫名其妙的校验错误。
  //
  // ⚠️ 过渡代码:daoJwt 30 天到期(最迟 2026-09-20),之后连同 `daoJwt`
  // 这个字段一起删。存的时候带着 "Bearer " 前缀,这里要剥掉。
  const daoJwt = account?.daoJwt
  if (daoJwt) return daoJwt.replace(/^Bearer\s+/i, '')

  return undefined
}

export function attachmentUrl(attachment: {url: string} | null | undefined) {
  return attachment ? `${RICE_SERVICE}${attachment.url}` : undefined
}

type RequestOptions = {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  body?: unknown
  query?: Record<string, string | number | undefined | null>
  /** 覆盖默认令牌 —— 登录后立刻用新令牌拉档案时用得上 */
  token?: string
  signal?: AbortSignal
}

export async function riceRequest<T>(
  path: string,
  {method = 'GET', body, query, token, signal}: RequestOptions = {},
): Promise<T> {
  const url = new URL(`${RICE_SERVICE}${path}`)
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined && value !== null && value !== '') {
        url.searchParams.set(key, String(value))
      }
    }
  }

  const headers: Record<string, string> = {Accept: 'application/json'}
  const auth = token ?? currentToken()
  if (auth) headers.Authorization = `Bearer ${auth}`

  let payload: BodyInit | undefined
  if (body instanceof FormData) {
    // 不设 Content-Type —— 让浏览器自己带上 multipart 的 boundary
    payload = body
  } else if (body !== undefined) {
    headers['Content-Type'] = 'application/json'
    payload = JSON.stringify(body)
  }

  const response = await fetch(url.toString(), {
    method,
    headers,
    body: payload,
    signal,
  })

  if (response.status === 204) return undefined as T

  const text = await response.text()
  let parsed: any = null
  if (text) {
    try {
      parsed = JSON.parse(text)
    } catch {
      // rice 的错误一律是 JSON;解析不了说明是网关之类返回的,按状态码处理
    }
  }

  if (!response.ok) {
    throw new RiceError(
      response.status,
      parsed?.errors ?? {detail: text || response.statusText},
    )
  }

  // 成功响应统一包在 {data: ...} 里;分页额外带 meta
  if (parsed && typeof parsed === 'object' && 'meta' in parsed)
    return parsed as T
  return (parsed?.data ?? parsed) as T
}
